import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { ifsService } from '../../../../services/ifsService';
import { vendorLinkService } from '../../../../services/vendorLinkService';
import { getActiveIFSAccountId } from '../../../../utils/ifsAccount';
import { getSenderUsage } from '../../../../utils/senderUsage';
import { MOCK_SENDERS } from '../mockShipmentData';
import { AddressCard, SearchInput } from '../IFSShippingDashboard.styles';
import Pager, { PAGE_SIZE } from '../Pager';
const SelectSenderStep = ({ isMockMode, sender, onSelect, onPicked }) => {
    const [addresses, setAddresses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('');
    const [page, setPage] = useState(0);
    const accountId = getActiveIFSAccountId();
    useEffect(() => {
        if (isMockMode) {
            setAddresses(MOCK_SENDERS.map((s, i) => ({ id: s.addressId || String(i), ...s })));
            setLoading(false);
            return;
        }
        // Cached (30 min, persisted across reloads) — same cache the Address Book uses
        ifsService.getClientAddressList().then(async (res) => {
            if (res.ok && res.data?.success) {
                const list = res.data.data.client_address || [];
                setAddresses(list);
                // Preselect the current user's linked default sender (if any and nothing chosen yet)
                if (!sender) {
                    if (accountId) {
                        try {
                            const linkRes = await vendorLinkService.getMyLink(accountId);
                            const link = linkRes.ok && linkRes.data?.success ? linkRes.data.links[0] : null;
                            if (link && list.some((a) => String(a.id) === link.clientAddressId)) {
                                // List items only carry partial fields — fetch the full record for the wizard
                                const detRes = await ifsService.getClientAddressData(link.clientAddressId);
                                const d = detRes.ok && detRes.data?.success ? detRes.data.data.client_address_data : null;
                                if (d) {
                                    onSelect({
                                        addressId: link.clientAddressId,
                                        name: d.name || '', company: d.company_name || '',
                                        address1: d.address1 || '', address2: d.address2 || '',
                                        city: d.city || '', state: d.state || '', zip: d.zip || '',
                                        country: d.country || 'US', phone: d.phone || '',
                                    });
                                }
                            }
                        }
                        catch { /* default sender is best-effort */ }
                    }
                }
            }
        }).finally(() => setLoading(false));
    }, [isMockMode]); // eslint-disable-line react-hooks/exhaustive-deps
    const handleSelect = (addr) => {
        const addressId = String(addr.id || addr.address_id || '');
        const mapped = {
            addressId,
            name: addr.name || addr.customer_name || '',
            company: addr.company || addr.company_name || addr.customer_company_name || '',
            address1: addr.address1 || addr.customer_address1 || '',
            address2: addr.address2 || addr.customer_address2 || '',
            city: addr.city || addr.customer_city || '',
            state: addr.state || addr.customer_state || '',
            zip: addr.zip || addr.customer_zip || '',
            country: addr.country || addr.customer_country || 'US',
            phone: addr.phone || addr.customer_phone || '',
        };
        onSelect(mapped);
        onPicked?.();
    };
    // Most-used senders first (usage tracked per IFS account each time one is picked)
    const usage = getSenderUsage(accountId);
    const filtered = (filter.trim().length >= 2
        ? addresses.filter(a => {
            const q = filter.trim().toLowerCase();
            return [a.name, a.company_name, a.company, a.address1, a.text]
                .some(v => typeof v === 'string' && v.toLowerCase().includes(q));
        })
        : addresses).slice().sort((a, b) => {
        const diff = (usage[String(b.id)] || 0) - (usage[String(a.id)] || 0);
        return diff !== 0 ? diff : String(a.name || '').localeCompare(String(b.name || ''));
    });
    const pageItems = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
    if (loading)
        return _jsx("p", { style: { color: 'var(--text-secondary)' }, children: "Loading sender addresses..." });
    return (_jsxs("div", { children: [_jsx("h3", { style: { color: 'var(--text-primary)', marginBottom: '0.5rem' }, children: "Select Sender Address" }), _jsx("p", { style: { color: 'var(--text-secondary)', fontSize: '0.875rem', marginBottom: '1.25rem' }, children: "Choose the sender address for this shipment. Most-used addresses appear first." }), _jsx("div", { style: { marginBottom: '1rem' }, children: _jsx(SearchInput, { style: { width: '100%' }, placeholder: "Filter by name, company, or address...", value: filter, onChange: e => { setFilter(e.target.value); setPage(0); } }) }), _jsx("div", { style: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(280px, 100%), 1fr))', gap: '0.75rem' }, children: pageItems.map((addr, i) => {
                    const isSelected = sender?.addressId === String(addr.id || addr.address_id || i);
                    return (_jsxs(AddressCard, { "$selected": isSelected, onClick: () => handleSelect(addr), children: [_jsx("h4", { children: addr.name || addr.customer_name || 'Address ' + (i + 1) }), _jsx("p", { children: addr.company || addr.company_name || addr.customer_company_name || '' }), _jsx("p", { children: addr.address1 || addr.customer_address1 || '' }), (addr.city || addr.customer_city) && (_jsxs("p", { children: [addr.city || addr.customer_city, ", ", addr.state || addr.customer_state || '', " ", addr.zip || addr.customer_zip || ''] })), usage[String(addr.id)] > 0 && (_jsxs("p", { style: { fontSize: '0.7rem', color: 'var(--accent-gold, #B8860B)', marginTop: '0.25rem' }, children: ["Used ", usage[String(addr.id)], "\u00D7"] }))] }, addr.id || i));
                }) }), _jsx(Pager, { page: page, total: filtered.length, onChange: setPage }), filtered.length === 0 && (_jsx("p", { style: { color: 'var(--text-secondary)' }, children: filter ? 'No addresses match your filter.' : 'No sender addresses found. Add addresses in the Address Book tab.' }))] }));
};
export default SelectSenderStep;
